/**
 * How water is drawn once it has been worked out where it lies.
 *
 * The shape of a pond is one problem and the look of it is another. This is the
 * second.
 *
 * What makes water read as water is not the colour. It is four things, and the
 * last version had one of them:
 *
 *   The surface moves, and moving means the normal moves. A flat sheet with a
 *   pattern drawn on it is a flat sheet with a pattern drawn on it. The normal
 *   is worked out here per pixel, from the slopes of the same waves that lift
 *   the surface, so the light lands differently everywhere and keeps changing.
 *
 *   It catches the sun. A glint off a wave is the single strongest thing that
 *   says water, and it comes from a sharp specular against a moving normal.
 *
 *   It goes from clear to opaque as it deepens, and from clear to a mirror as
 *   the angle flattens. That second one is Fresnel: looking straight down you
 *   see the bottom, looking out across it you see the sky. Water that is
 *   equally transparent everywhere reads as coloured glass.
 *
 *   And it breaks white where it meets the land. Foam at the shore, moving in
 *   and out, is what tells somebody where the edge is.
 *
 * Two things are worked out per corner when the mesh is built and everything
 * else comes from them: how far that corner is from the nearest bank, and how
 * fast the water there is going. Still and running water are the same shader,
 * because a river is water that is going somewhere and a pond is water that is
 * not.
 */

import * as THREE from 'three';
import { BED_LOW, BED_SPAN, SHORE_REACH } from './shore';

/**
 * How shallow the water has to be for foam, in tiles of depth.
 *
 * Measured in depth rather than in distance from the bank, because that is what
 * decides where a wave breaks. Half a tile of water is about knee deep on this
 * scale, which is where the white starts.
 */
const FOAM_DEPTH = 1.25;

/**
 * How far apart the ribbons of it are, in tiles of depth.
 *
 * Two or three of them inside the band at any moment, all of them walking in at
 * the sand. In depth, so they lie along the shore's own contours: they bunch up
 * against a steep bank and spread out over a shelving one, which is what waves
 * do.
 */
const FOAM_SPACING = 0.34;

/** How fast they walk in, in tiles of depth a second. */
const FOAM_SPEED = 0.22;

/**
 * How coarse the lace in it is, in waves to the tile.
 *
 * Both scales are held above a tile across. Anything finer is a pattern with
 * fewer pixels than it has features from anywhere but a nose on the sand, and a
 * pattern like that does not read as foam, it reads as a broken television.
 */
const FOAM_LACE = 0.34;
const FOAM_LACE_FINE = 0.78;

/** How fast the lace itself churns, in tiles a second. */
const FOAM_CHURN = 0.5;

/** How shallow the water is where the last of it washes up the sand. */
const RIPPLE_DEPTH = 0.45;

/**
 * How fine the froth in the white is, in waves to the tile.
 *
 * Finer than the lace that breaks the band into pieces, and coarser than a
 * pixel: this is the difference between a band of white that reads as foam and
 * one that reads as a reflection.
 */
const FOAM_FROTH = 1.9;

/**
 * How far apart a pond's rings are, in tiles, and how far in from the bank they
 * reach on a pond and on the sea.
 *
 * Along the distance to the bank rather than along the depth. The depth was the
 * obvious thing to run them off, since a ring should follow the bank round, and
 * it is what put a fault into every shallow body in the world: ground is
 * written in thirty-seconds of a tile and comes out as wide level shelves, so a
 * whole shelf is one depth, and one depth is one point on the ring. A lake a
 * pace deep was drawn as a patchwork of rounded squares, each of them a shelf
 * of the bed showing through as a ring that had been stretched across it.
 *
 * The distance to the bank is smooth wherever the bank is, which is what the
 * shore map was built to be, and rings drawn along it follow the bank round
 * without knowing anything about what the bottom is doing.
 */
const RIPPLE_SPACING = 1.35;
const RIPPLE_REACH = 2.6;
const RIPPLE_REACH_OPEN = 9.0;

/** A water material, with the things that change each frame reachable. */
export interface WaterMaterial extends THREE.MeshLambertMaterial {
  waterTime?: { value: number };
  waterSun?: { value: THREE.Vector3 };
  /** How bright the sun is, so the glint goes out at night. */
  waterLight?: { value: number };
  /** The map of where the land is, and the stretch of world it covers. */
  waterShore?: { value: THREE.Texture | null };
  waterBed?: { value: THREE.Texture | null };
  waterBedTexels?: { value: number };
  waterBedRect?: { value: THREE.Vector4 };
  waterShoreRect?: { value: THREE.Vector4 };
}

/**
 * The material every piece of water in the world is drawn with.
 *
 * Lambert underneath, so water is lit by the same sun as everything else and
 * takes the same fog, with the surface written into it rather than replacing
 * it.
 */
export function waterMaterial(): WaterMaterial {
  const material: WaterMaterial = new THREE.MeshLambertMaterial({
    vertexColors: true,
    transparent: true,
    flatShading: false,
    depthWrite: false,
    side: THREE.DoubleSide,
  });

  const time = { value: 0 };
  const sun = { value: new THREE.Vector3(0.45, 0.78, 0.44).normalize() };
  const light = { value: 1 };

  // The map of where the land is. Shared by every water surface, since they are
  // all in the same world and all want the same answer.
  const shore = { value: null as THREE.Texture | null };
  const shoreRect = { value: new THREE.Vector4(0, 0, 1, 1) };
  const shoreReach = { value: SHORE_REACH };
  const bed = { value: null as THREE.Texture | null };
  const bedTexels = { value: 1 };
  const bedRect = { value: new THREE.Vector4(0, 0, 1, 1) };

  material.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = time;
    shader.uniforms.uSun = sun;
    shader.uniforms.uLight = light;
    shader.uniforms.uShore = shore;
    shader.uniforms.uShoreRect = shoreRect;
    shader.uniforms.uShoreReach = shoreReach;
    shader.uniforms.uBed = bed;
    shader.uniforms.uBedTexels = bedTexels;
    shader.uniforms.uBedRect = bedRect;

    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
attribute float aShore;
attribute vec2 aFlow;
/**
 * Whether this surface may lift at all.
 *
 * The far sea is one flat sheet cut into pieces tens of tiles wide, and a swell
 * a few tiles long sampled at its corners is not a swell, it is a sheet tilting.
 * So the far sea says nought here and stays flat, and keeps the moving surface
 * that is drawn on it rather than built into it.
 */
attribute float aSwell;
/** Which way the nearest bank lies, for the swell that runs at it. */
attribute vec2 aToShore;
/** One where this is lava rather than water, which changes almost everything. */
attribute float aMolten;
/** How open the water this belongs to is: an ocean, a lake, or a puddle. */
attribute float aOpen;
uniform float uTime;
varying float vShore;
varying float vChurn;
varying vec2 vSurface;
varying vec3 vToEye;
varying vec2 vToShore;
varying float vMolten;
varying float vOpen;
varying vec3 vWorld;`,
      )
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
vShore = aShore;

// Still water works slowly at itself; running water works at whatever speed it
// is running. One number, so a river and a pond can share a mesh.
float rate = length(aFlow);
vChurn = 0.22 + rate * 2.6;
vMolten = aMolten;
vOpen = aOpen;

// Lava is heavy. It works at itself at a fraction of the rate water does, which
// is most of what makes it read as molten rock rather than as orange water.
vChurn = mix(vChurn, 0.05 + rate * 0.5, aMolten);

// Where this corner is on the water rather than in the world: a river carries
// its surface with it, so the pattern is read from a point sliding upstream.
vec3 placed = (modelMatrix * vec4(transformed, 1.0)).xyz;
vSurface = placed.xz - aFlow * uTime;
vToShore = aToShore;

// The lift, held back at the bank so water meets the ground rather than
// hovering over it.
//
// Long, because these are waves on a sea rather than ripples on a puddle. At a
// wave number of 1.7 a crest arrives every three and a half tiles, which at the
// height a character stands is not a wave at all: it is a texture, and a fine
// enough one to read as noise. Roughly forty tiles between crests is a swell
// somebody can watch roll past. Taller with it, since a wave that long and that
// shallow is a flat sheet.
/*
 * The lift, which is the same swell the surface is shaded by.
 *
 * Its phase is the distance to the bank, so the geometry rises and falls along
 * the same lines the shading does and the two agree about where a crest is.
 * Held back right at the bank so water meets the ground rather than hovering
 * over it, and held back again out in the open, where the far sea is one flat
 * sheet cut into pieces tens of tiles wide and lifting its corners would tilt
 * whole sheets rather than raise a wave.
 */
float held = clamp(aShore * 0.9, 0.0, 1.0) * aSwell;
float inshore = 1.0 - smoothstep(6.0, 70.0, aShore);
float swell = (0.16 + rate * 0.12) * inshore * inshore;
float lift = sin(aShore * 0.3 - uTime * vChurn * 1.5) + sin(aShore * 0.62 - uTime * vChurn * 2.3) * 0.36;
transformed.y += lift * swell * held;

/*
 * Where this bit of water is, for the sky in it and the sun off it.
 *
 * The position rather than the direction. The far sea is one sheet cut into
 * pieces tens of tiles across, and a direction worked out at four corners and
 * interpolated across a piece that size is not the direction to anything: it
 * swings smoothly over the whole piece and drags the Fresnel and the glint round
 * with it, which is what put rings of banding out on the open water. Worked out
 * per pixel from the position, it is right everywhere.
 */
vWorld = placed;

vToEye = normalize(cameraPosition - placed);`,
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
uniform float uTime;
uniform vec3 uSun;
uniform float uLight;

/**
 * The map of where the land is, and the stretch of world it covers.
 *
 * Read per pixel, because the sea is one sheet with a vertex every thirty six
 * tiles and no attribute on it can describe a coastline.
 */
uniform sampler2D uShore;
uniform vec4 uShoreRect;
uniform float uShoreReach;
uniform sampler2D uBed;
uniform float uBedTexels;
uniform vec4 uBedRect;

varying float vShore;
varying float vChurn;
varying vec2 vSurface;
varying vec3 vToEye;
varying vec2 vToShore;
varying float vMolten;
varying float vOpen;
varying vec3 vWorld;

/**
 * How much of a wave survives being drawn at this size.
 *
 * A wave is only a wave while there are pixels enough to draw one. Past that it
 * beats against the pixel grid and comes back as rings and moire, which under
 * perspective gather round the horizon. So each scale is faded out as the pixels
 * drawing it run short.
 *
 * Judged on the footprint rather than the distance, because at a grazing angle
 * one pixel covers a great deal of water at no distance at all.
 */
float resolved(float k, float footprint) {
  float perWave = (6.2831853 / k) / max(footprint, 0.0001);
  return smoothstep(2.5, 7.0, perWave);
}

/**
 * A steady direction for a point on the grid, which is where the noise comes
 * from.
 *
 * A direction rather than a number, and that is the whole of the difference
 * between this and what was here before. Noise built from a number per corner
 * has its largest values in the middles of its cells and its smallest at the
 * corners, so the cells themselves are what it draws: from above, a grid of
 * rounded squares. On a colour that is a faint patchwork. On a surface normal,
 * which is the slope of the thing and not the thing, it is a lattice of blobs
 * lit from the side, and it was drawn across every stretch of open water in the
 * world.
 *
 * Built from a direction per corner, the noise is nought at every corner and
 * carries no trace of where the corners are. It costs one more multiply and it
 * is the difference between water and a tiled floor.
 */
vec2 slopeAt(vec2 at) {
  vec3 held = fract(vec3(at.xyx) * vec3(0.1031, 0.1030, 0.0973));
  held += dot(held, held.yzx + 33.33);
  vec2 drawn = fract((held.xx + held.yz) * held.zy) * 2.0 - 1.0;
  return normalize(drawn + vec2(0.0001, 0.0001));
}

/**
 * Gradient noise, with its slopes.
 *
 * The slopes come back with it because the surface has to be lit, and taking
 * them by sampling the noise three times costs three times as much as having
 * the noise hand them over.
 *
 * Eased to the fifth rather than the third, so the slope is smooth across a
 * cell edge as well as inside it: a cubic ease leaves a crease along every edge,
 * and a field of creases on a square lattice is, again, a grid.
 *
 * The value comes back moved into nought to one, since everything that reads it
 * was written against a noise that ran that way.
 */
vec3 noised(vec2 at) {
  vec2 cell = floor(at);
  vec2 into = at - cell;

  vec2 eased = into * into * into * (into * (into * 6.0 - 15.0) + 10.0);
  vec2 easing = 30.0 * into * into * (into - 1.0) * (into - 1.0);

  vec2 ga = slopeAt(cell);
  vec2 gb = slopeAt(cell + vec2(1.0, 0.0));
  vec2 gc = slopeAt(cell + vec2(0.0, 1.0));
  vec2 gd = slopeAt(cell + vec2(1.0, 1.0));

  float va = dot(ga, into);
  float vb = dot(gb, into - vec2(1.0, 0.0));
  float vc = dot(gc, into - vec2(0.0, 1.0));
  float vd = dot(gd, into - vec2(1.0, 1.0));

  float corners = va - vb - vc + vd;
  float value = va + eased.x * (vb - va) + eased.y * (vc - va) + eased.x * eased.y * corners;

  vec2 slope =
    ga +
    eased.x * (gb - ga) +
    eased.y * (gc - ga) +
    eased.x * eased.y * (ga - gb - gc + gd) +
    easing * (vec2(eased.y, eased.x) * corners + vec2(vb, vc) - va);

  return vec3(value * 0.62 + 0.5, slope * 0.62);
}

/**
 * The open ocean.
 *
 * Not waves from anywhere. A sum of a handful of wave trains is a sum of a
 * handful of directions, and a handful of directions is what the eye finds
 * first: whatever the spacing, the water reads as three or four sources
 * crossing rather than as a sea. Out where there is no coast to be running at,
 * the sea is not going anywhere in particular, so what is wanted is a surface
 * with no direction in it at all.
 *
 * So it is noise on noise. Four scales, each half the height and twice the
 * frequency of the one under it, each turned by the same fixed angle so no two
 * of them share an axis, and each drifting its own way at its own rate so
 * nothing in the sum holds still long enough to become a direction. The slopes
 * come through the turns with it, which is what the second matrix is for: a
 * gradient carried through a rotation has to be turned back the other way.
 */
vec3 openWater(vec2 at, float drift, float footprint, float tighter) {
  const mat2 turn = mat2(0.8, 0.6, -0.6, 0.8);
  const mat2 back = mat2(0.8, -0.6, 0.6, 0.8);

  // Turned from the start. The first scale is the widest and the heaviest, so
  // it is the one whose lattice shows: read along the world's own axes it laid
  // a grid of rounded squares a dozen tiles across over every stretch of open
  // water, which from the bank read as the bottom showing through in patches.
  mat2 through = turn;
  mat2 backThrough = back;

  vec3 sum = vec3(0.0);

  /*
   * And the ground each scale is read on is pushed about by a slow reading of
   * itself.
   *
   * Turning the lattice is not enough on its own: a single scale of this noise
   * is a regular tiling of blobs whichever way it faces, and turning it gives a
   * diagonal tiling instead of a square one. What breaks a tiling is bending
   * the ground under it, so each scale after the first is read at a point
   * shifted by this, and further each time. The cells stop lining up with each
   * other and the pattern stops being a pattern.
   */
  vec2 pushed = vec2(noised(at * 0.011).x, noised(at * 0.011 + 41.3).x) - vec2(0.5);

  /*
   * The largest features are about fifty five tiles across, and each octave
   * halves that. Long, because this is a sea.
   *
   * A lake is not a short sea: the same swell drawn across a body a tenth the
   * width reads as one enormous wave sitting in a puddle. So a smaller body
   * gets the same water at a tighter scale, which is chop rather than swell.
   */
  float scale = 0.018 * tighter;
  float weight = 1.0;

  for (int octave = 0; octave < 4; octave++) {
    // Its own way and its own rate, so the scales never march together.
    vec2 way = vec2(cos(float(octave) * 2.4), sin(float(octave) * 2.4));
    float held = weight * resolved(scale * 6.2831853, footprint);

    vec3 read = noised(
      through * at * scale +
      way * drift * (0.05 + float(octave) * 0.035) +
      pushed * float(octave) * 1.3
    );

    sum.x += (read.x - 0.5) * held;
    sum.yz += (backThrough * read.yz) * held * scale;

    through = through * turn;
    backThrough = backThrough * back;
    scale *= 2.1;
    weight *= 0.5;
  }

  /*
   * The slope, lifted well above what the heights alone would give.
   *
   * A wave this long is, in truth, this shallow: the literal gradient of a fifty
   * tile swell a tile and a half tall is almost nothing, and a surface lit by it
   * is a sheet of glass with a very slight ripple. Measured, it came out at five
   * hundredths, against six tenths for the swell running at the shore, so the
   * open sea simply could not be seen.
   *
   * So the height and the slope part company here on purpose. The height is what
   * the water is doing, and is what the colour and the foam read. The slope is
   * what it looks like it is doing, which for water seen from a few tiles away
   * is a good deal more. Nothing downstream needs the two to agree.
   */
  // Less of it on a small body. The sea is lit by a swell fifty tiles long and
  // can carry a slope that strong; a lake given the same reads as oil rather
  // than water, in pale blotches the size of a house.
  sum.yz *= mix(4.2, 13.0, vOpen);

  return sum;
}

/**
 * The swell running at the shore: where a crest is, from minus one to one.
 *
 * The phase is the distance to the bank, so a crest is a line of equal distance
 * from it: along the coast however the coast bends, round a headland, and closed
 * into a ring about an island. Time runs the phase backwards, so the whole train
 * travels inward.
 *
 * On its own clock rather than the body's. How fast a swell comes in is about
 * the sea, not about how briskly the water it is crossing happens to be moving.
 */
float swellAt(float shore, float bend) {
  // Plus, not minus. A crest sits where the phase is fixed, so with the time
  // added the distance to the bank has to fall as time runs on: the crest walks
  // towards the shore. Taken away it does the opposite, and the whole sea very
  // convincingly ran backwards out to the horizon.
  float p1 = (shore + bend) * 0.3 + uTime * 0.92;
  float p2 = (shore + bend * 0.6) * 0.62 + uTime * 1.55;
  return sin(p1) * 0.78 + sin(p2) * 0.22;
}

/**
 * The surface of the water: what it is doing here, and which way it is tilted.
 *
 * Two things, blended by how near the shore is.
 *
 * Close in, one swell running at the land. Its crests are the lines of equal
 * distance from the bank, so they lie along the coast however the coast bends,
 * wrap round a headland the way a real swell refracts, and close into a ring
 * round an island; and its phase runs down with that distance, so the crests
 * travel inward. That is a single wave rather than a direction picked per
 * point, which is what stops it turning through every angle out where every
 * bank is equally far away.
 *
 * Further out, the open sea, which is going nowhere and is made of noise.
 *
 * The two are crossed over across a wide band, so there is no line on the water
 * where one becomes the other.
 */
vec3 waterAt(
  vec2 at,
  float drift,
  vec2 toShore,
  float shore,
  float footprint,
  out float crest,
  out float inshoreHere
) {
  // How much of this is the shore's business. Full at the beach, gone by the
  // time the coast is a long way behind.
  float inshore = 1.0 - smoothstep(4.0, 62.0, shore);
  inshore *= inshore;
  inshoreHere = inshore;
  crest = 0.0;

  // Tighter on a small body, up to four times, and the sea's own scale on the sea.
  vec3 open = openWater(at, drift, footprint, mix(4.0, 1.0, vOpen));

  // Nothing else to say out here.
  if (inshore < 0.004) return vec3(open.x, open.yz);

  /*
   * The swell.
   *
   * The phase is the distance to the bank, so a crest is a contour of that
   * distance and the whole train moves inward as time runs on. Two of them, a
   * long one and a shorter one riding on it, and both bent along the coast by a
   * slow noise so a beach does not get a wave train ruled with a straight edge.
   */
  /*
   * Bent along the coast, but only a little: the crests are meant to follow the
   * contours of the shore, and a big offset here is what stops them doing it.
   * A tile and a half, over a hundred tiles of coast, so one stretch of beach
   * gets its waves a moment before the next.
   */
  float bend = (noised(at * 0.012).x - 0.5) * 3.0;
  vec2 up = length(toShore) > 0.01 ? -normalize(toShore) : vec2(0.0);

  crest = swellAt(shore, bend);

  float k1 = 0.3;
  float w1 = 1.1 * inshore * resolved(k1, footprint);

  float height = crest * w1;

  // The slope of a wave whose phase is the shore distance runs along the way
  // that distance grows, which is straight out to sea. Taken from the crest by
  // stepping it a little, so the two can never disagree about where a wave is.
  float ahead = swellAt(shore + 0.5, bend);
  vec2 slope = up * ((ahead - crest) * 2.0 * w1);

  // The open sea is still under it, quieter where the swell is loudest.
  float under = 1.0 - inshore * 0.55;

  return vec3(open.x * under + height, open.yz * under + slope);
}`,
      )
      .replace(
        '#include <dithering_fragment>',
        `#include <dithering_fragment>

float drift = uTime * vChurn;

// How much water one pixel covers, which is what decides how much of the wave
// pattern can be drawn at all.
vec2 across = fwidth(vSurface);
float footprint = max(across.x, across.y);

/*
 * Where the land is from here, read off the map rather than off the mesh.
 *
 * Outside the map there is no land worth knowing about, which is the honest
 * answer: the map covers everything the reader can see, and the swell has faded
 * to nothing well inside its edge.
 */
vec2 onMap = (vWorld.xz - uShoreRect.xy) / uShoreRect.zw;
float shoreHere = uShoreReach;
vec2 towardLand = vec2(0.0);



if (onMap.x > 0.0 && onMap.x < 1.0 && onMap.y > 0.0 && onMap.y < 1.0) {
  vec4 readShore = texture2D(uShore, onMap);
  shoreHere = readShore.r * uShoreReach;
  towardLand = readShore.gb * 2.0 - 1.0;
}

float crest = 0.0;
float inshore = 0.0;
vec3 held = waterAt(vSurface, drift, towardLand, shoreHere, footprint, crest, inshore);

/*
 * The texture on a small body, which is the only surface it has.
 *
 * A pond has no swell and no fetch, so everything above leaves it nearly flat.
 * The open water pattern is drawn four times finer on a small body, and at the
 * distance the game is played from there is nothing left of it to draw; and the
 * swell's phase is the distance to the bank, which across ten paces of pond is
 * less than one wave. What was left of it read as a painted tile.
 *
 * What it gets instead is the fine stipple the wind puts on still water. Taken
 * from the same noise everything else here is built from, so it has no
 * direction: an earlier version used a pair of sine trains crossing at an
 * angle, and two sine trains have an angle whether or not anybody wanted one.
 * A pond read as though it were being blown along.
 *
 * The rings that run in from the bank are further down, written against the
 * depth, because the depth is not known yet here.
 */
float ponded = 1.0 - vOpen;
if (ponded > 0.004) {
  const float stippleK = 3.4;
  float seen = resolved(stippleK, footprint) * ponded;

  vec3 stipple = noised(vSurface * stippleK * 0.16 + drift * 0.05);

  held.x += (stipple.x - 0.5) * 0.05 * seen;

  // The slope, lifted the way the open water's is and for the same reason: the
  // literal gradient of a ripple a finger high is almost nothing, and a surface
  // lit by it is a sheet of glass.
  held.y += stipple.y * 5.5 * seen;
  held.z += stipple.z * 5.5 * seen;
}

/*
 * The surface normal, from the slopes of those same waves. Flatter out in the
 * open and sharper where the water is running, since a stream is choppier than
 * a pond.
 *
 * Seven times what it was, because the waves are seven times longer. A slope is
 * the wave number times the height, so lengthening a wave without touching this
 * would have left a surface with no tilt in it anywhere: the right shape, lit
 * like a sheet of glass. The two numbers move together or neither of them is
 * worth changing.
 */
float steepness = 0.95 + vChurn * 0.6;
vec3 surface = normalize(vec3(-held.y * steepness, 1.0, -held.z * steepness));

// Worked out here rather than carried from the corners, so it is the direction
// to this pixel rather than a direction swung smoothly across a whole sheet.
vec3 eye = normalize(cameraPosition - vWorld);
float toEye = max(dot(surface, eye), 0.0);

/*
 * Lava, which is the same surface with almost none of the same answers.
 *
 * It does not reflect the sky, it does not glint, and it is not see through. It
 * makes its own light: the deep of it glows and the skin over it has gone dark,
 * so what is drawn is a black crust broken by cracks of orange, and the cracks
 * are where the surface is moving. The waves are the same waves, which is what
 * makes it lie in a crater the way water lies in a lake, but they are read as a
 * crust breaking rather than as a surface rippling.
 */
if (vMolten > 0.5) {
  float heat = clamp(held.x * 0.28 + 0.5, 0.0, 1.0);

  // The crust: broad slow patches that have cooled, with the cracks between
  // them where the rock is still pulling apart.
  float crust =
    smoothstep(0.34, 0.62, heat + (noised(vSurface * 0.7 + drift * 0.12).x - 0.5) * 0.44);

  vec3 cold = vec3(0.11, 0.07, 0.07);
  vec3 warm = vec3(0.62, 0.13, 0.03);
  vec3 hot = vec3(1.0, 0.55, 0.10);
  vec3 white = vec3(1.0, 0.86, 0.52);

  vec3 molten = mix(cold, warm, smoothstep(0.0, 0.45, heat));
  molten = mix(molten, hot, smoothstep(0.45, 0.8, heat));
  molten = mix(molten, white, smoothstep(0.86, 1.0, heat) * 0.7);

  // The crust darkens it back down, and the last of the heat comes through the
  // cracks whatever the light of day is doing: this is the light, not lit.
  gl_FragColor.rgb = mix(molten, cold, crust * 0.72);
  gl_FragColor.rgb += hot * (1.0 - crust) * 0.35;

  // The rim where it meets the rock, which glows rather than foams.
  float lip = 1.0 - smoothstep(0.0, 2.2, shoreHere);
  gl_FragColor.rgb += vec3(1.0, 0.42, 0.12) * lip * lip * 0.55;

  gl_FragColor.a = 1.0;
  return;
}

// Fresnel: looking down into it you see through it, looking out across it you
// see the sky. Without this water is coloured glass at every angle.
float sky = pow(1.0 - toEye, 3.5);
gl_FragColor.rgb = mix(gl_FragColor.rgb, vec3(0.58, 0.72, 0.86), sky * 0.4);
gl_FragColor.a = clamp(gl_FragColor.a + sky * 0.28, 0.0, 1.0);

/*
 * The sun off it, which on water is glitter rather than a highlight.
 *
 * This was a power of two hundred and twenty, which is a mirror. A mirror shows
 * the sun exactly where the surface faces it and nowhere else, and a smooth
 * analytic wave field faces it along smooth curves: the open water came out
 * ruled with clean concentric rings, which is the one thing water never looks
 * like. Real glitter is broken up because a real sea is broken up at a scale
 * finer than anything drawn here.
 *
 * Spread wide enough to be a sheen rather than a line, and quiet, and out
 * altogether at night. Loud and broad it is not a glint, it is a searchlight,
 * and the sea came out looking like it was being shelled.
 */
vec3 halfway = normalize(eye + uSun);
float glint = pow(max(dot(surface, halfway), 0.0), 42.0);

// The sun off a wave nobody can resolve is a sun off nothing. Faded with the
// waves that carry it, or the glitter goes on beating against the pixels long
// after the water it is supposed to be on has gone smooth.
glint *= smoothstep(3.5, 9.0, (6.2831853 / 0.55) / max(footprint, 0.0001));

// Broken up, so the highlights are scattered over the water rather than laid
// out on it. A perfectly even wave field gives perfectly even glints, and an
// even field of glints is the single thing that reads most obviously as a
// repeating texture: the eye finds the lattice in the reflections long before
// it finds it in the waves themselves.
float scatter = noised(vSurface * 0.16 + drift * 0.03).x;
scatter = 0.25 + 0.75 * scatter * scatter;
gl_FragColor.rgb += vec3(1.0, 0.97, 0.88) * glint * scatter * 0.3 * uLight;

// A little of the wave height in the colour as well, so the crests read as
// crests when the sun is not on them.
gl_FragColor.rgb += held.x * (0.007 + vChurn * 0.007);

/*
 * The swell, in the colour as well as in the surface.
 *
 * A wave tilting the normal is a wave you can only see when the light happens
 * to be catching it, and from a few tiles up over open water it very often is
 * not. What actually reads as a wave rolling in is a band of paler water moving
 * across the darker water behind it, so the crest is lightened directly. The
 * band is the top of the wave rather than the whole of it, which is why the
 * step starts above the middle: a sine lightened everywhere is a surface that
 * pulses, not a wave that travels.
 */
float rolling = smoothstep(0.35, 0.95, crest) * inshore;
gl_FragColor.rgb += vec3(0.05, 0.075, 0.085) * rolling * (0.25 + 0.75 * uLight);
gl_FragColor.a = clamp(gl_FragColor.a + rolling * 0.06, 0.0, 1.0);

/*
 * How deep the water is here.
 *
 * The ground under this pixel, read out of the picture of it, against the
 * surface of the water at this pixel, which the mesh already carries. Their
 * difference is the depth, and where the depth reaches nought is the waterline:
 * not the edge of a tile, not a distance worked out from tiles, but the line
 * where the ground actually comes up through the water.
 *
 * The picture is read sharp and mixed here, because the height is split across
 * two bytes and a blur between texels would wrap the low one.
 */
float vDeep = 64.0;

/*
 * The bed has a stretch of its own, shorter than the map's and drawn finer.
 *
 * The waterline is worked out from this picture and from nothing else, so how
 * sharp the water's edge is is exactly how many texels there are to a tile. At
 * the map's own two a tile the edge could only be placed to within half a tile
 * and it wandered in and out of the sand; this is drawn at four and a half.
 */
vec2 onBed = (vWorld.xz - uBedRect.xy) / uBedRect.zw;

if (onBed.x > 0.0 && onBed.x < 1.0 && onBed.y > 0.0 && onBed.y < 1.0) {
  vec2 atTexel = onBed * uBedTexels - 0.5;
  vec2 corner = floor(atTexel);
  vec2 into = atTexel - corner;

  float readAt00 = 0.0;
  float readAt10 = 0.0;
  float readAt01 = 0.0;
  float readAt11 = 0.0;

  vec2 step1 = vec2(1.0) / uBedTexels;

  vec4 bed00 = texture2D(uBed, (corner + vec2(0.5, 0.5)) * step1);
  vec4 bed10 = texture2D(uBed, (corner + vec2(1.5, 0.5)) * step1);
  vec4 bed01 = texture2D(uBed, (corner + vec2(0.5, 1.5)) * step1);
  vec4 bed11 = texture2D(uBed, (corner + vec2(1.5, 1.5)) * step1);

  readAt00 = (bed00.r * 255.0 * 256.0 + bed00.g * 255.0) / 65535.0;
  readAt10 = (bed10.r * 255.0 * 256.0 + bed10.g * 255.0) / 65535.0;
  readAt01 = (bed01.r * 255.0 * 256.0 + bed01.g * 255.0) / 65535.0;
  readAt11 = (bed11.r * 255.0 * 256.0 + bed11.g * 255.0) / 65535.0;

  float alongTop = mix(readAt00, readAt10, into.x);
  float alongBottom = mix(readAt01, readAt11, into.x);
  float ground = mix(alongTop, alongBottom, into.y) * ${BED_SPAN.toFixed(1)} + ${BED_LOW.toFixed(1)};

  vDeep = vWorld.y - ground;
}

/*
 * The sheet gives out where the water does.
 *
 * At the waterline, which is where the depth reaches nought, and not at the
 * edge of the tiles the sheet happens to be built from. Every earlier attempt
 * at this measured how far away land was and faded on that; a distance worked
 * out from tiles carries the tiles with it, which is why the surf kept wrapping
 * round squares with rounded corners instead of following the water's edge.
 *
 * Ten hundredths of a tile of depth, so the sheet ends on a line rather than
 * over a stretch.
 */
gl_FragColor.a *= smoothstep(0.0, 0.1, vDeep);

/*
 * And the foam, in the shallows.
 *
 * Shallow, not near: foam is what a wave does when it runs out of water under
 * it, so where it goes is decided by how deep the water is and not by how far
 * off the sand is. On a shelving beach that puts a wide band of surf; against
 * a steep bank it puts a narrow one; and either way the band follows the line
 * where the ground comes up rather than the edge of a tile.
 */
float band = 1.0 - smoothstep(0.0, ${FOAM_DEPTH.toFixed(2)}, vDeep);

/*
 * Where along the coast this is, which is the thing the surf was missing.
 *
 * Everything about a ribbon is worked out from how far the bank is, and every
 * point the same distance from the bank gets the same answer: the whole island
 * broke at the same instant and went bare at the same instant, and measured
 * over an hour the surf swung between covering a hundredth of the water and
 * covering a fiftieth of it. Which is a ring being switched on and off.
 *
 * So the phase is shifted by a reading of where this is in the world. It has to
 * wander faster than a stretch of coast somebody can see at once: over twenty
 * tiles it still left the whole beach in front of them going bare together for
 * half a second at a time, which measured as the surf dropping to a sixth of
 * itself and back. Over nine tiles, with more than a full turn of phase across
 * it, there are always waves arriving somewhere in view.
 *
 * Still nine tiles rather than one: it is smooth enough that no two pixels
 * disagree, and it is anchored in the world rather than driven by the clock, so
 * it cannot turn into grain however long the world has been running.
 */
float alongCoast = (noised(vSurface * 0.11).x - 0.5) * 9.5;

// Walking in: a phase that rises with distance and with time is a pattern whose
// crests sit at ever smaller distances as time passes, which is to say a
// pattern moving at the land.
float ribbonAt =
  vDeep * ${(6.2831853 / FOAM_SPACING).toFixed(4)} +
  alongCoast +
  uTime * ${((6.2831853 / FOAM_SPACING) * FOAM_SPEED).toFixed(4)};

/*
 * How much of any of this can be drawn from here.
 *
 * The same question the open water asks of every one of its octaves, asked at
 * last of the surf. What cannot be drawn is not faded to nothing, which would
 * leave a coast with no surf on it at all: it is faded towards an even wash,
 * so a shore seen from high up is a soft pale edge rather than a seething one.
 */
float ribbonSeen = resolved(${(6.2831853 / FOAM_SPACING).toFixed(4)}, footprint);
float laceSeen = resolved(${(FOAM_LACE_FINE * 6.2831853).toFixed(4)}, footprint);
float coarseSeen = resolved(${(FOAM_LACE * 6.2831853).toFixed(4)}, footprint);

// Thin, and softened as the pixels run out so the edge of a ribbon is an edge
// rather than a staircase.
float edgeOf = mix(0.4, 0.0, ribbonSeen);
float ribbon = smoothstep(0.22 - edgeOf, 0.88, sin(ribbonAt));
ribbon = mix(0.55, ribbon, ribbonSeen);

// A second set, half as often and a little slower, so they overtake each other
// and the beach does not tick.
float slower = smoothstep(0.35 - edgeOf, 0.95, sin(ribbonAt * 0.46 - uTime * 0.42));
slower = mix(0.3, slower, ribbonSeen);

/*
 * Broken along its length.
 *
 * Foam is lace, not paint. The lace drifts, so the holes in it are never in the
 * same place twice and nothing tells you where the next wave will break, and it
 * drifts by an amount that is the same for every pixel on the screen. That last
 * part is the one that matters: this used to be shifted along the direction of
 * the nearest land, which is a per pixel reading, multiplied by a clock that
 * counts up forever. Two pixels side by side sampled a mile apart after a
 * quarter of an hour, and the result was noise in the strict sense.
 */
vec2 churn = vec2(0.41, -0.27) * (uTime * ${FOAM_CHURN.toFixed(2)});
float lace =
  noised(vSurface * ${FOAM_LACE.toFixed(3)} + churn).x * 0.62 +
  noised(vSurface * ${FOAM_LACE_FINE.toFixed(3)} + churn * 1.7).x * 0.38;

// Widened as it stops being drawable, and then given up altogether: an even
// wash is what surf looks like from far off, and is what will not sparkle.
lace = smoothstep(0.24 - mix(0.28, 0.0, laceSeen), 0.66, lace);
// Never all the way out. Foam with every hole closed is paint and foam with
// every hole open is nothing, so the lace is held between the two.
lace = 0.18 + 0.82 * mix(0.55, lace, max(coarseSeen, laceSeen));

float foam = clamp(ribbon + slower * 0.6, 0.0, 1.0) * band * lace;

/*
 * And the very edge, where the water is thin enough that there is more foam
 * than water.
 *
 * This is the part that has to be there whatever the waves are doing. Ribbons
 * come and go, and measured over a quarter of an hour the stretch of beach in
 * front of somebody went from covered to bare and back: without a line of white
 * at the waterline itself, the sand simply meets clear water for seconds at a
 * time, which no beach does. Narrow, so it is a line rather than a rim.
 */
foam = clamp(foam + (1.0 - smoothstep(0.0, 0.4, vDeep)) * lace * 0.62, 0.0, 1.0);

/*
 * Pale rather than painted. White at full strength is a solid to be walked on,
 * and this is water with air in it.
 *
 * And lit, which it was not: the colour was set outright, so at midnight the
 * surf stayed the white it is at noon and a dark coast was ringed with the one
 * thing in the picture still in full daylight. Foam is water with air in it and
 * it is as dark as the night is. A little is left at the bottom, because
 * breaking water catches what light there is before anything else does.
 */
vec3 froth = vec3(0.93, 0.97, 1.0) * (0.16 + 0.84 * uLight);
/*
 * Stopped at the waterline itself, so nothing is drawn across dry ground, but
 * over a short enough stretch that the bands it holds back survive.
 *
 * The surface reaches a little past the water it stands for, and everything
 * written in depth is at its strongest where the depth runs out; so the rings
 * and the wash, which have no waterline of their own, were drawn at full
 * weight over the bank. On the sea the surf covers it. Round a pond it was a
 * pale collar lying on the grass.
 */
float wetted = smoothstep(0.0, 0.06, vDeep);
foam *= wetted;

/*
 * Broken into froth, so a band of white reads as water with air in it.
 *
 * A smooth white band is a smooth white thing, and a smooth white thing on
 * water is a reflection. What tells the two apart is that foam has a grain: it
 * is thousands of bubbles, and even at a distance where none of them can be
 * drawn it is never even. So the band is cut with two more scales of the same
 * lace, finer than the holes that break it up, and both of them bow out as they
 * stop being drawable rather than turning into speckle.
 */
float fineSeen = resolved(${(FOAM_FROTH * 6.2831853).toFixed(4)}, footprint);
float fineFroth =
  noised(vSurface * ${FOAM_FROTH.toFixed(2)} + churn * 2.4).x * 0.6 +
  noised(vSurface * ${(FOAM_FROTH * 2.3).toFixed(2)} - churn * 1.3).x * 0.4;

// Never all the way down: this is a texture in the white, not a second set of
// holes in it.
foam *= mix(1.0, 0.42 + 0.58 * smoothstep(0.18, 0.82, fineFroth), fineSeen * 0.85);

/*
 * And only where there is a sea to break.
 *
 * A pond has no swell to run out of depth, so it gets no surf at all: what it
 * has is the water moving, and a ring where that movement meets the bank. A
 * lake gets some of both. The sea gets the whole of it.
 */
foam *= vOpen;

/*
 * What a pond has instead: rings running out from its edge.
 *
 * Along the depth, the same as the surf, so they follow the bank round rather
 * than crossing it. Quiet enough to read as the water moving and not as a
 * second kind of foam, and strongest on the small bodies that have no surf of
 * their own.
 */
/*
 * Held to the rim on the small bodies.
 *
 * How far in from the bank they run. A pond gets a ring round its edge; the sea
 * gets a wider band of them, because there is more water to be moving.
 */
float ringBand = mix(${RIPPLE_REACH.toFixed(2)}, ${RIPPLE_REACH_OPEN.toFixed(2)}, vOpen);

/*
 * Broken up, so a ring is a ripple and not a contour line.
 *
 * The phase is how far the bank is, which is smooth, so on its own this draws a
 * set of perfectly even bands. Real ripples running in off a bank are uneven
 * along their length: they arrive at slightly different moments a few paces
 * apart. So the phase is nudged by a slow noise across the surface, which bends
 * each ring without moving it off the contour it belongs to.
 */
float ringBend = (noised(vSurface * 0.5 + drift * 0.04).x - 0.5) * 2.6;

float ringing = sin(
  shoreHere * ${(6.2831853 / RIPPLE_SPACING).toFixed(4)} + ringBend - uTime * 1.35
);

float rings =
  smoothstep(0.1, 0.9, ringing) *
  (1.0 - smoothstep(0.0, ringBand, shoreHere)) *
  (1.0 - vOpen * 0.72) *
  wetted *
  resolved(${(6.2831853 / RIPPLE_SPACING).toFixed(4)}, footprint);

// Stronger than they were. On a body with no surf these are the whole of what
// the water is doing, and at the weight the sea's own trimming left them they
// could not be seen at all.
gl_FragColor.rgb += froth * rings * mix(0.2, 0.13, vOpen);
gl_FragColor.a = clamp(gl_FragColor.a + rings * mix(0.09, 0.06, vOpen), 0.0, 1.0);

/*
 * And a pond is water you can see into.
 *
 * A body a dozen paces across is a foot deep and clear to the bottom; the sea
 * is neither. Painted at the sea's own weight a pond came out as a bright
 * opaque disc with a white ring round it, which is a puddle of milk. So the
 * smaller the body the more of the bed shows through it.
 */
gl_FragColor.a *= mix(0.82, 1.0, vOpen);

/*
 * And deep water is not see-through.
 *
 * Water is clear in the shallows and opaque well before it is out of its depth,
 * which is worth having for its own sake and fixes something else besides: the
 * ground under the sea arrives a square at a time, and while the sea was clear
 * to the bottom everywhere, a square arriving or leaving changed the colour of
 * the water over it. That read as pale rectangles drifting about the open
 * water, on the grid, appearing and going as somebody walked.
 *
 * Past a few tiles of depth there is nothing under it worth seeing anyway.
 *
 * Over a long stretch of depth rather than a short one. Closing it up quickly
 * put a line round every island where the shelf drops away, which is one
 * artefact traded for another: the shelf is short, so a fade that finishes in
 * two tiles of depth finishes in a couple of paces of ground.
 */
gl_FragColor.a = mix(gl_FragColor.a, 1.0, smoothstep(0.5, 7.0, vDeep));

// Carrying its own weight. The water under it has been faded out towards the
// waterline, so foam that only tinted what was there came out as a whisper: it
// has to bring its own opacity with it or there is nothing for it to tint.
gl_FragColor.rgb = mix(gl_FragColor.rgb, froth, foam * 0.82);
gl_FragColor.a = clamp(gl_FragColor.a + foam * 0.62, 0.0, 1.0);

// And the last of it washing up the sand behind the foam, which is thinner and
// runs a little further than the foam does.
float reach = clamp(1.0 - vDeep / ${RIPPLE_DEPTH.toFixed(2)}, 0.0, 1.0);

// Nearly nothing on a pond. This is the sheet a spent wave leaves on the sand,
// and a body with no surf has no spent waves; at the sea's weight it drew a
// white collar round every pond, which is the thing it is there to avoid.
float wash = reach * reach * lace * wetted * mix(0.25, 1.0, vOpen);

gl_FragColor.rgb += wash * 0.05 * (0.2 + 0.8 * uLight);
gl_FragColor.a = clamp(gl_FragColor.a + wash * 0.04, 0.0, 1.0);

/*
 * And into the fog, like everything else.
 *
 * All of the above is written where the dithering goes, which is past where the
 * fog is mixed in, so the fog was worked out and then painted over: the ocean
 * was the one thing in the world that did not fade with distance. It stayed
 * bright and detailed out to the camera's far plane and was cut off dead
 * against the fogged sky, which read as a straight seam lying across the sea
 * with a different colour beyond it.
 *
 * The same mix the fog chunk does, on the depth the fog chunk hands over.
 */
#ifdef USE_FOG
  #ifdef FOG_EXP2
    float intoFog = 1.0 - exp(-fogDensity * fogDensity * vFogDepth * vFogDepth);
  #else
    float intoFog = smoothstep(fogNear, fogFar, vFogDepth);
  #endif
  gl_FragColor.rgb = mix(gl_FragColor.rgb, fogColor, clamp(intoFog, 0.0, 1.0));
#endif
`,
      );
  };

  // Kept where the frame loop can reach them without knowing any of the above.
  material.waterTime = time;
  material.waterSun = sun;
  material.waterLight = light;
  material.waterShore = shore;
  material.waterBed = bed;
  material.waterBedTexels = bedTexels;
  material.waterBedRect = bedRect;
  material.waterShoreRect = shoreRect;
  return material;
}

/**
 * Hands every water surface the map of where the land is.
 *
 * Not called `use` anything, however well that would read: the lint that keeps
 * React hooks honest takes any function starting with those three letters for
 * one of its own, and this is a class method's helper in a renderer.
 */
export function giveShoreMap(
  surfaces: readonly (THREE.Mesh | null)[],
  map: {
    texture: THREE.Texture;
    bed: THREE.Texture;
    rect: THREE.Vector4;
    bedRect: THREE.Vector4;
  } | null,
  texels: number,
  bedTexels: number,
): void {
  for (const surface of surfaces) {
    const material = surface?.material as WaterMaterial | undefined;
    if (!material?.waterShore || !material.waterShoreRect) continue;

    material.waterShore.value = map?.texture ?? null;
    if (material.waterBed) material.waterBed.value = map?.bed ?? null;
    if (material.waterBedTexels) material.waterBedTexels.value = bedTexels;
    if (map) material.waterShoreRect.value.copy(map.rect);
    if (map && material.waterBedRect) material.waterBedRect.value.copy(map.bedRect);
  }
}

/** Moves water surfaces on by the time that has passed. */
export function stepWater(surfaces: readonly (THREE.Mesh | null)[], seconds: number): void {
  for (const surface of surfaces) {
    const material = surface?.material as WaterMaterial | undefined;
    if (material?.waterTime) material.waterTime.value += seconds;
  }
}

/** Where the sun is and how bright, so the water catches it and stops at night. */
export function aimWater(
  surfaces: readonly (THREE.Mesh | null)[],
  towards: THREE.Vector3,
  brightness: number,
): void {
  for (const surface of surfaces) {
    const material = surface?.material as WaterMaterial | undefined;
    material?.waterSun?.value.copy(towards).normalize();
    if (material?.waterLight) material.waterLight.value = Math.max(0, Math.min(1, brightness));
  }
}

/**
 * Which way a body of water is going, and how fast.
 *
 * There is nothing in the data that says a river is a river, and there does not
 * need to be: a river is water that is long in one direction and narrow in the
 * other, and a pond is not. Anything that reads as a channel is given a drift
 * along its length, and everything else lies still.
 */
export function flowOf(tiles: readonly number[], width: number): { x: number; z: number } {
  if (tiles.length < 12) return { x: 0, z: 0 };

  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;

  for (const index of tiles) {
    const tx = index % width;
    const tz = Math.floor(index / width);
    if (tx < minX) minX = tx;
    if (tx > maxX) maxX = tx;
    if (tz < minZ) minZ = tz;
    if (tz > maxZ) maxZ = tz;
  }

  const across = maxX - minX + 1;
  const along = maxZ - minZ + 1;
  const longest = Math.max(across, along);
  const shortest = Math.min(across, along);

  // Wide enough to be a lake, whatever its outline.
  if (longest < shortest * 2.5) return { x: 0, z: 0 };

  // The narrower the channel, the faster it reads as running.
  const speed = Math.min(0.5, 0.12 + (longest / Math.max(1, shortest)) * 0.04);
  return across > along ? { x: speed, z: 0 } : { x: 0, z: speed };
}
